/**
 * Emit hosts for the `payments` analytics group — 11 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';

export function paywallCheckoutClickedFromFreeToPaid() {
  trackLegacyEvent({
    name: SegmentObjectName.Paywall,
    props: {
      action: SegmentActionName.PaywallCheckoutClickedFromFreeToPaid,
      target_plan: SubscriptionTier.PRO,
      // [4/5] getSegmentContext() removed with the model's state; it was
      // provably always undefined (setSegmentContext had zero dispatchers).
      paywall_context: undefined,
    },
  });

  // V2 — paywall_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.AccountToPro,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromAccountToPro() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.AccountToPro,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromAskAI() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.AskAI,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromExtendProTrial() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.ExtendProTrial,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromNoTeamMemberPage() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.NoTeamMemberPage,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

// Paywall
//
// `paywallCheckout` and `paywallAddOnCheckout` are now
// `trackPaywallCheckout` / `trackPaywallAddOnCheckout` in
// `@/lib/analytics/commerce/checkout-events`. They had no callers besides
// the two checkout mutation hooks, which is why no delegate stayed behind.
export function paywallModalOpenFromProActionOnFree() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.ProActionOnFree,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromQuotaExceeded() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.QuotaExceeded,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromSessionHeader() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.SessionHeader,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromSidebar() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.Sidebar,
      target_plan: SubscriptionTier.PRO,
    },
  });
}

export function paywallModalOpenFromVisibilityToTeam() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: PaywallSource.TemplateVisibilityToTeam,
      target_plan: SubscriptionTier.PRO_PLUS,
    },
  });
}

export function trackPaywallViewed(input: {
  source: PaywallSource;
  target_plan: SubscriptionTier;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PaywallViewed,
    props: {
      source: input.source,
      target_plan: input.target_plan,
    },
  });
}
